import { useState, useCallback } from "react";
import * as api from "../../api";

/* PASO 4 (F3B) — Gestión de usuarios (admin) + historial de cliente.
 * Movido VERBATIM de App.js. */
export function useAdminUsers(showToast, user) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [deleteUserConfirm, setDeleteUserConfirm] = useState(null);
  const [statusChangeModal, setStatusChangeModal] = useState(null);
  const [statusChangeReason, setStatusChangeReason] = useState("");
  const [clientHistory, setClientHistory] = useState(null);
  const [clientHistoryLoading, setClientHistoryLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    if (user?.role !== "admin") return;
    setLoadingUsers(true);
    try {
      const list = await api.listUsers();
      setUsers(list);
    } catch (err) {
      showToast("Error", err.message, "error");
    } finally {
      setLoadingUsers(false);
    }
  }, [user, showToast]);

  const handleChangeUserStatus = async (userId, newStatus, reason) => {
    try {
      const updated = await api.updateUserStatus(userId, newStatus, reason);
      setUsers(prev => prev?.map(u => u.id === updated.id ? updated : u));
      const labels = { active: "reactivado", suspended: "suspendido", blocked: "bloqueado" };
      showToast("Estado actualizado", `Usuario ${labels[newStatus] || newStatus}`, "success");
      setStatusChangeModal(null);
      setStatusChangeReason("");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const handleToggleUserRole = async (targetUser) => {
    const nextRole = targetUser.role === "reseller" ? "client" : "reseller";
    if (!window.confirm(`¿Cambiar el rol de ${targetUser.email} a ${nextRole === "reseller" ? "Revendedor (Mayorista)" : "Cliente estándar"}?`)) return;
    try {
      const updated = await api.updateUserRole(targetUser.id, nextRole);
      setUsers(prev => prev?.map(u => u.id === updated.id ? updated : u));
      showToast("Rol actualizado", `${updated.email}: ${nextRole === "reseller" ? "Revendedor" : "Cliente"}`, "success");
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const requestStatusChange = (targetUser, newStatus) => {
    if (newStatus === "active") {
      handleChangeUserStatus(targetUser.id, "active");
    } else {
      setStatusChangeReason(targetUser.status_reason || "");
      setStatusChangeModal({ user: targetUser, newStatus });
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserConfirm) return;
    try {
      const res = await api.deleteUser(deleteUserConfirm.id);
      setUsers(prev => prev?.filter(u => u.id !== deleteUserConfirm.id));
      showToast("Usuario Eliminado", res.orders_preserved > 0 ? `${res.orders_preserved} pedido(s) preservados para auditoría` : "Eliminado con éxito", "success");
      setDeleteUserConfirm(null);
    } catch (err) {
      showToast("Error", err.message, "error");
    }
  };

  const openClientHistory = async (userId) => {
    if (!userId) return;
    setClientHistoryLoading(true);
    setClientHistory({ user: null, stats: null, orders: [] });
    try {
      const data = await api.adminUserHistory(userId);
      setClientHistory(data);
    } catch (err) {
      showToast("Error", err.message || "No se pudo cargar el historial", "error");
      setClientHistory(null);
    } finally {
      setClientHistoryLoading(false);
    }
  };

  return {
    users, loadingUsers, loadUsers, userRoleFilter, setUserRoleFilter,
    userSearch, setUserSearch, deleteUserConfirm, setDeleteUserConfirm,
    statusChangeModal, setStatusChangeModal, statusChangeReason, setStatusChangeReason,
    clientHistory, setClientHistory, clientHistoryLoading,
    handleChangeUserStatus, handleToggleUserRole, requestStatusChange,
    handleDeleteUser, openClientHistory,
  };
}
